import { partitionRegisterRows, type ReconcileRowLike } from './reconcile-panes';

/**
 * Builds the QuickBooks-shaped "Reconciliation Detail" report (#429).
 *
 * Pure: the page hands it the two register windows and gets back a fully
 * totalled structure, so the arithmetic — which is what an auditor actually
 * checks — is unit-testable without rendering anything.
 *
 * The report's spine is three sections and the balances between them:
 *
 *   Beginning Balance                        (the reconciliation's own)
 *     Cleared Transactions      → Cleared Balance   = beginning + cleared
 *     Uncleared Transactions    → Register Balance  = cleared  + uncleared
 *     New Transactions          → Ending Balance    = register + new
 *
 * "Uncleared" is everything on or before the statement date that didn't clear —
 * deliberately NOT limited to the statement period, because the outstanding
 * checks that matter most are the stale ones from earlier months. "New" is
 * anything dated after the statement date, which is why the ending balance can
 * differ from the statement's.
 */

export interface ReconcileDetailRow extends ReconcileRowLike {
	occurredOn: number;
	checkNumber?: string;
	reference?: string;
	counterparty?: string;
	memo?: string;
	clearedAt?: number | string | null;
	reconciliationId?: string | null;
}

export interface DetailLine<T extends ReconcileDetailRow = ReconcileDetailRow> {
	row: T;
	/** Running total WITHIN this group — matches QuickBooks, which restarts the
	 *  Balance column at each "Checks and Payments" / "Deposits and Credits". */
	balanceCents: number;
	cleared: boolean;
}

export interface DetailGroup<T extends ReconcileDetailRow = ReconcileDetailRow> {
	title: string;
	lines: DetailLine<T>[];
	totalCents: number;
}

export interface DetailSection<T extends ReconcileDetailRow = ReconcileDetailRow> {
	title: string;
	groups: DetailGroup<T>[];
	totalCents: number;
}

export interface ReconciliationDetail<T extends ReconcileDetailRow = ReconcileDetailRow> {
	beginningBalanceCents: number;
	cleared: DetailSection<T>;
	clearedBalanceCents: number;
	uncleared: DetailSection<T>;
	registerBalanceCents: number;
	newTransactions: DetailSection<T>;
	endingBalanceCents: number;
}

export const PAYMENTS_TITLE = 'Checks and Payments';
export const DEPOSITS_TITLE = 'Deposits and Credits';

/**
 * Did this row clear as part of THIS reconciliation?
 *
 * A row stamped with a different reconciliation belongs to that one, even
 * though it carries a `clearedAt`. Shared with the reconcile page's tick
 * seeding so the on-screen ticks and the printed report can never disagree
 * about what "cleared" meant.
 */
export const isClearedForReconciliation = (
	row: Pick<ReconcileDetailRow, 'clearedAt' | 'reconciliationId'>,
	reconciliationId: string,
): boolean => {
	if (!row.clearedAt) return false;
	if (row.reconciliationId && row.reconciliationId !== reconciliationId) return false;
	return true;
};

/** Child payment of a deposit slip, as the register attaches them. */
export interface RegisterChildLike {
	counterparty?: string;
	reference?: string;
	memo?: string;
}

export interface DescribableRow {
	kind: string;
	counterparty?: string;
	memo?: string;
	paymentCount?: number;
	payments?: readonly RegisterChildLike[];
}

/** How many payers to name before collapsing to a count. */
const MAX_NAMED_PAYERS = 3;

/**
 * The "Name" cell for a register row.
 *
 * A deposit slip has no counterparty of its own — it's one bank line covering
 * many payments — so it rendered as a bare "Deposit" with an empty name, which
 * is useless when you're matching it against a statement. The register already
 * attaches the child payments, so name the payers instead, falling back to a
 * count once there are too many to fit.
 *
 * Everything else keeps the existing counterparty-then-memo order.
 */
export const registerRowDescription = (r: DescribableRow): string => {
	const counterparty = r.counterparty?.trim();
	if (counterparty) return counterparty;

	if (r.kind === 'deposit') {
		// Deduped, order preserved: one account can pay twice in the same deposit
		// (two cheques, dues + a fine), and "7499HG, 7499HG" reads like a bug.
		// The question this column answers is WHO paid, not how many times.
		const payers = [...new Set(
			(r.payments ?? [])
				.map((p) => p.counterparty?.trim())
				.filter((s): s is string => !!s),
		)];
		const count = r.paymentCount ?? payers.length;
		if (payers.length > 0) {
			const named = payers.slice(0, MAX_NAMED_PAYERS).join(', ');
			const rest = payers.length - MAX_NAMED_PAYERS;
			// The memo, when there is one, is extra context on top of the payers.
			const label = rest > 0 ? `${named} +${rest} more` : named;
			const memo = r.memo?.trim();
			return memo ? `${label} · ${memo}` : label;
		}
		const memo = r.memo?.trim();
		if (memo) return memo;
		// Nothing identifiable — at least say how many payments made up the slip.
		if (count > 0) return `${count} payment${count === 1 ? '' : 's'}`;
		return '';
	}

	return r.memo?.trim() ?? '';
};

/**
 * The "Num" cell for a register row. A deposit slip carries no check number of
 * its own, but a SINGLE-payment slip effectively does — the payer's check —
 * which is exactly what a bank statement shows for it.
 */
export const registerRowNumber = (
	r: DescribableRow & { checkNumber?: string; reference?: string },
): string => {
	const own = r.checkNumber?.trim() || r.reference?.trim();
	if (own) return own;
	if (r.kind === 'deposit') {
		const children = r.payments ?? [];
		if (children.length === 1) return children[0].reference?.trim() ?? '';
	}
	return '';
};

const buildGroup = <T extends ReconcileDetailRow>(
	title: string,
	rows: readonly T[],
	isCleared: (r: T) => boolean,
): DetailGroup<T> => {
	let running = 0;
	const lines = rows.map((row) => {
		running += row.signedAmountCents;
		return { row, balanceCents: running, cleared: isCleared(row) };
	});
	return { title, lines, totalCents: running };
};

const buildSection = <T extends ReconcileDetailRow>(
	title: string,
	rows: readonly T[],
	isCleared: (r: T) => boolean,
): DetailSection<T> => {
	const { payments, deposits } = partitionRegisterRows(rows);
	// Empty groups are dropped rather than printed as "0 items" — QuickBooks
	// omits them and an auditor reads a printed zero as a finding.
	const groups = [
		buildGroup(PAYMENTS_TITLE, payments, isCleared),
		buildGroup(DEPOSITS_TITLE, deposits, isCleared),
	].filter((g) => g.lines.length > 0);
	return { title, groups, totalCents: groups.reduce((s, g) => s + g.totalCents, 0) };
};

/**
 * Date only — no secondary key. `Array.sort` is stable, so same-day rows keep
 * the register's own order, which is what the QuickBooks report does. Adding an
 * amount tiebreak here reorders same-day deposits and makes the printed Balance
 * column disagree with the register the user just ticked through.
 */
const byDate = <T extends ReconcileDetailRow>(a: T, b: T): number => a.occurredOn - b.occurredOn;

export interface BuildDetailInput<T extends ReconcileDetailRow> {
	reconciliationId: string;
	beginningBalanceCents: number;
	/** Statement end, a business date (midnight UTC ms). */
	periodEnd: number;
	/** Every register row dated ON OR BEFORE `periodEnd`, any age. */
	throughPeriodEnd: readonly T[];
	/** Every register row dated AFTER `periodEnd`. */
	afterPeriodEnd: readonly T[];
}

export const buildReconciliationDetail = <T extends ReconcileDetailRow>(
	input: BuildDetailInput<T>,
): ReconciliationDetail<T> => {
	const { reconciliationId, beginningBalanceCents, throughPeriodEnd, afterPeriodEnd } = input;
	const isCleared = (r: T) => isClearedForReconciliation(r, reconciliationId);

	const clearedRows = throughPeriodEnd.filter(isCleared).slice().sort(byDate);
	const unclearedRows = throughPeriodEnd.filter((r) => !isCleared(r)).slice().sort(byDate);
	const newRows = afterPeriodEnd.slice().sort(byDate);

	const cleared = buildSection('Cleared Transactions', clearedRows, isCleared);
	const uncleared = buildSection('Uncleared Transactions', unclearedRows, isCleared);
	const newTransactions = buildSection('New Transactions', newRows, isCleared);

	const clearedBalanceCents = beginningBalanceCents + cleared.totalCents;
	const registerBalanceCents = clearedBalanceCents + uncleared.totalCents;
	const endingBalanceCents = registerBalanceCents + newTransactions.totalCents;

	return {
		beginningBalanceCents,
		cleared,
		clearedBalanceCents,
		uncleared,
		registerBalanceCents,
		newTransactions,
		endingBalanceCents,
	};
};
